const TWITCH_USERNAME_REGEX = /^[a-z0-9_]{4,25}$/;

const LIVE_NOTIFICATION_FEATURES = Object.freeze({
  auto_delete_previous: {
    label: "Auto delete previous announcements",
    description:
      "Automatically delete the previous announcement when the streamer goes live again.",
  },
  show_tags: {
    label: "Show first five tags",
    description:
      "Display the first five Twitch tags as disabled buttons under the announcement.",
  },
  auto_edit_end: {
    label: "Automatically edit when stream ends",
    description:
      "Edit the announcement once the stream ends to disable the Twitch button and link to the VOD if available.",
  },
});

const DROP_NOTIFICATION_FEATURES = Object.freeze({
  show_game_embed: {
    label: "Show the campaign overview embed",
    description:
      "Send a short embed with the game art, drop window, and directory link before the detailed reward embed.",
  },
});

const TWITCH_DROP_API_URL = "https://twitch-drops-api.sunkwi.com/drops";
const YOUTUBE_CHANNEL_ID_REGEX = /^UC[a-zA-Z0-9_-]{22}$/i;

const sanitizeTwitchUsername = (username) => {
  return String(username ?? "").trim().toLowerCase();
};

const isValidTwitchUsername = (username) => {
  return TWITCH_USERNAME_REGEX.test(username);
};

const normalizeTwitchUsername = (username) => {
  const sanitized = sanitizeTwitchUsername(username);
  return isValidTwitchUsername(sanitized) ? sanitized : null;
};

const stripQueryAndHash = (value) => value.split(/[?#]/)[0];

const decodeSegment = (segment) => {
  try {
    return decodeURIComponent(segment || "").trim();
  } catch {
    return segment || "";
  }
};

const ensureHandleFormat = (value) => {
  const normalized = value.startsWith("@") ? value : `@${value}`;
  return normalized.split(/[/?]/)[0];
};

const isYouTubeHost = (hostname) => {
  const normalized = (hostname || "").toLowerCase();
  return (
    normalized === "youtu.be" ||
    normalized === "www.youtu.be" ||
    normalized.endsWith("youtube.com")
  );
};

const parseYouTubePathSegments = (input) => {
  if (!input) {
    return [];
  }

  let target = input;
  try {
    const parsedUrl = new URL(input);
    if (isYouTubeHost(parsedUrl.hostname)) {
      target = parsedUrl.pathname;
    }
  } catch {
    // Not a URL, use the raw value.
  }

  const cleaned = stripQueryAndHash(target)
    .replace(/^[\s/]+|[\s/]+$/g, "")
    .trim();
  if (!cleaned) {
    return [];
  }

  return cleaned.split("/").filter(Boolean);
};

const parseYouTubeChannelIdentifier = (input) => {
  const raw = String(input ?? "").trim();
  const result = { raw };

  if (!raw) {
    return result;
  }

  if (YOUTUBE_CHANNEL_ID_REGEX.test(raw)) {
    result.channelId = raw;
    return result;
  }

  const segments = parseYouTubePathSegments(raw);
  if (segments.length === 0) {
    return result;
  }

  const [firstSegment, secondSegment] = segments;
  const normalizedFirst = firstSegment.toLowerCase();

  if (normalizedFirst === "channel" && secondSegment) {
    const candidate = decodeSegment(secondSegment);
    if (YOUTUBE_CHANNEL_ID_REGEX.test(candidate)) {
      result.channelId = candidate;
      return result;
    }
  }

  if (firstSegment.startsWith("@")) {
    result.handle = ensureHandleFormat(firstSegment);
    return result;
  }

  if (
    (normalizedFirst === "c" || normalizedFirst === "user") &&
    secondSegment
  ) {
    result.customName = decodeSegment(secondSegment);
    return result;
  }

  result.customName = decodeSegment(firstSegment);
  return result;
};

const requireFetch = (fetchImpl) => {
  if (typeof fetchImpl === "function") {
    return fetchImpl;
  }

  if (typeof fetch === "function") {
    return fetch;
  }

  throw new Error(
    "A fetch implementation is required to resolve YouTube channel identifiers.",
  );
};

const fetchChannelIdWithParam = async (apiKey, param, value, fetchImpl) => {
  const url = new URL("https://www.googleapis.com/youtube/v3/channels");
  url.searchParams.set("part", "id");
  url.searchParams.set("key", apiKey);
  url.searchParams.set(param, value);

  const response = await fetchImpl(url);
  if (!response.ok) {
    if (response.status === 400) {
      return null;
    }
    throw new Error(
      `YouTube channel lookup failed (${param}): ${response.status}`,
    );
  }

  const data = await response.json();
  return data?.items?.[0]?.id ?? null;
};

const fetchChannelIdBySearch = async (apiKey, query, fetchImpl) => {
  const url = new URL("https://www.googleapis.com/youtube/v3/search");
  url.searchParams.set("part", "snippet");
  url.searchParams.set("type", "channel");
  url.searchParams.set("maxResults", "5");
  url.searchParams.set("q", query);
  url.searchParams.set("key", apiKey);

  const response = await fetchImpl(url);
  if (!response.ok) {
    throw new Error(`YouTube search lookup failed: ${response.status}`);
  }

  const data = await response.json();
  const candidate = data?.items?.find(
    (entry) => entry?.snippet?.channelId || entry?.id?.channelId,
  );
  return candidate?.snippet?.channelId || candidate?.id?.channelId || null;
};

const resolveYouTubeChannelId = async (
  input,
  apiKey,
  fetchImpl = undefined,
) => {
  const parsed = parseYouTubeChannelIdentifier(input);
  if (!parsed.raw) {
    return null;
  }

  if (parsed.channelId) {
    return parsed.channelId;
  }

  const key = String(apiKey ?? "").trim();
  if (!key) {
    return null;
  }

  const fetchFn = requireFetch(fetchImpl);
  const lookups = [];
  if (parsed.handle) {
    lookups.push({
      type: "handle",
      value: parsed.handle.replace(/^@+/, ""),
    });
  }
  if (parsed.customName) {
    lookups.push({ type: "username", value: parsed.customName });
  }
  lookups.push({ type: "search", value: parsed.raw });

  for (const lookup of lookups) {
    if (lookup.type === "handle") {
      const handleResult = await fetchChannelIdWithParam(
        key,
        "forHandle",
        lookup.value,
        fetchFn,
      );
      if (handleResult) {
        return handleResult;
      }
      continue;
    }

    if (lookup.type === "username") {
      const usernameResult = await fetchChannelIdWithParam(
        key,
        "forUsername",
        decodeSegment(lookup.value),
        fetchFn,
      );
      if (usernameResult) {
        return usernameResult;
      }
      continue;
    }

    if (lookup.type === "search") {
      const searchResult = await fetchChannelIdBySearch(
        key,
        lookup.value,
        fetchFn,
      );
      if (searchResult) {
        return searchResult;
      }
    }
  }

  return null;
};

export {
  TWITCH_USERNAME_REGEX,
  sanitizeTwitchUsername,
  isValidTwitchUsername,
  normalizeTwitchUsername,
  LIVE_NOTIFICATION_FEATURES,
  DROP_NOTIFICATION_FEATURES,
  TWITCH_DROP_API_URL,
  YOUTUBE_CHANNEL_ID_REGEX,
  parseYouTubeChannelIdentifier,
  resolveYouTubeChannelId,
};

export default {
  TWITCH_USERNAME_REGEX,
  sanitizeTwitchUsername,
  isValidTwitchUsername,
  normalizeTwitchUsername,
  LIVE_NOTIFICATION_FEATURES,
  DROP_NOTIFICATION_FEATURES,
  TWITCH_DROP_API_URL,
  YOUTUBE_CHANNEL_ID_REGEX,
  parseYouTubeChannelIdentifier,
  resolveYouTubeChannelId,
};
